import { inject, Injectable } from '@angular/core';
import { ConfigService } from '../../../services/config.service';
import { UrlStateService } from '../../../services/url-state.service';
import { AuthService } from '../../../services/auth.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { InfoButton } from '../../../models/client-configuration';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { Util } from '../../../util';
import { ActionType } from '../../../models/action';
import { Router } from '@angular/router';
import { HelpButtonDialogFacade } from 'src/app/_core/state/dialogs/help-button/help-button.facade';
import { LoginDialogComponent } from '../../../login/components/login-dialog/login-dialog.component';
import { openWindow } from '../../../_core/utils/open-window';

@Injectable({
  providedIn: 'root'
})
export class HeaderService {
  private readonly helpButtonDialogFacade = inject(HelpButtonDialogFacade);

  private readonly customLoginUrl: string | undefined;
  private readonly isUserGuest: boolean;
  // TODO change this by config store
  readonly isAlertBannerEnabled$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly authService: AuthService,
    private readonly configService: ConfigService,
    private readonly dialog: MatDialog,
    private readonly urlStateService: UrlStateService,
    private readonly apiService: ApiService,
    private readonly router: Router,
  ) {
    this.customLoginUrl = this.configService.get().customLoginUrl;
    this.isUserGuest = this.configService.get().inviteGuestAccountActive && this.customLoginUrl !== null;
  }

  openBookClub() {
    const { enableBookClubButtonToSection, bookClubUrl } = this.configService.get();

    // Go to opac internal book club section
    if (enableBookClubButtonToSection) {
      localStorage.setItem('redirectTo', this.router.createUrlTree(['/user/experiences']).toString());
      this.router.navigate(['/user/experiences']);
      return;
    }

    localStorage.setItem('redirectTo', bookClubUrl);
    localStorage.setItem('redirectToOle', 'Y');

    // If user is already logged in
    const userId = this.authService.getLoggedUser()?.id;
    if (userId) {
      return this.goToBookClub();
    }

    if (this.customLoginUrl) {
      this.externalLogin(true);
      return;
    }

    this.openBookClubLoginDialog();
  }

  openHelp() {
    window.open(this.configService.get().helpUrl, '_blank', 'noopener');
  }

  openInfo(button: InfoButton) {
    if (button.link) {
      window.open(button.link);
      return;
    }


    this.helpButtonDialogFacade.openDialog(button.textButton, button.textContent);
  }

  guestLogin() {
    this.openLoginDialog();
  }

  login() {
    if (this.customLoginUrl) {
      this.externalLogin();
      return;
    }

    this.openLoginDialog();
  }

  signUp() {
    const { customSignUpUrl } = this.configService.get();

    // TODO Remove this code block when backend fixed
    if (this.configService.isZinkers()) {
      window.open('https://zinkers.fundacionrepsol.com/signup', '_blank', 'noopener');
      return;
    }

    // Without custom url just open our modal
    if (!customSignUpUrl) {
      this.openLoginDialog(true);
      return;
    }

    // Get 'utm' params and open the url
    openWindow(this.getUrlWithUtmParams(customSignUpUrl));
  }

  externalLogin(isBookClubMode?: boolean) {
    const getBecasSantanderUrl = (url: string): string => {
      if (!this.configService.isLearningRoomBecasSantander()) {
        return url;
      }

      return this.getUrlWithUtmParams(url);
    };

    this.authService.getExternalLoginUrl(this.configService.get().customLoginUrl).subscribe((url) => {
      let newUrl = url;
      newUrl = getBecasSantanderUrl(newUrl);

      if (isBookClubMode) {
        this.goToBookClub();
        return;
      }

      window.location.href = newUrl;
    });
  }

  private openBookClubLoginDialog() {
    this.dialog
      .open(LoginDialogComponent)
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          const action = Util.getAction();
          if (action) {
            Util.setAction({
              ...action,
              type: ActionType.NONE,
            });
          } else {
            Util.removeAction();
          }
        } else {
          this.goToBookClub();
        }
      });
  }

  private goToBookClub() {
    const userId = this.authService.getLoggedUser()?.id;
    const params = {
      patronId: userId,
    };

    this.apiService.getLinkToBookclub(params).subscribe((url) => (window.location.href = url));
  }

  private openLoginDialog(showRegisterForm = false) {
    this.dialog
      .open(LoginDialogComponent, {
        data: { screen: showRegisterForm ? 'signup' : 'login' },
      })
      .afterClosed()
      .subscribe((result: boolean) => {
        if (result) {
          window.location.reload();
        } else {
          const action = Util.getAction();
          if (action) {
            Util.setAction({
              ...action,
              type: ActionType.NONE,
            });
          } else {
            Util.removeAction();
          }
        }
      });
  }

  // TODO: Refactor and move this and login.state:getUrlWithUtmParams into new service or something
  private getUrlWithUtmParams(url: string): string {
    const utmParams = this.urlStateService.getAndStoreParams('utm');
    if (utmParams.size === 0) {
      return url;
    }

    const searchParams = new URLSearchParams(window.location.search);

    utmParams.forEach((v, k) => {
      if (!searchParams.has(k)) {
        searchParams.set(k, v);
      }
    });

    return `${url}?${searchParams.toString()}`;
  }
}
